import React, { useEffect, useState } from 'react';
import { appointmentService } from '../../services/appointmentService';
import { useLanguage } from '../../hooks/useLanguage';
import { formatDay, todayKey } from './appointmentUtils';

interface Props {
    date: string;
    value: string;
    duration?: number;
    onDateChange: (date: string) => void;
    onChange: (timeSlot: string) => void;
}

interface SlotRow { time: string; available: boolean }

// Choix d'un jour puis d'un créneau libre (calculé par le serveur : horaires, RDV existants, durée)
export const SlotPicker: React.FC<Props> = ({ date, value, duration, onDateChange, onChange }) => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const [slots, setSlots] = useState<SlotRow[]>([]);
    const [closed, setClosed] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!date) return;
            setLoading(true);
            try {
                const res = await appointmentService.getAvailableSlots(date, duration);
                if (cancelled) return;
                setSlots(res.data?.slots || []);
                setClosed(!!res.data?.closed);
            } catch {
                if (!cancelled) { setSlots([]); setClosed(false); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [date, duration]);

    const morning = slots.filter(s => s.time < '12:00');
    const afternoon = slots.filter(s => s.time >= '12:00');

    const group = (label: string, rows: SlotRow[]) => rows.length > 0 && (
        <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{label}</p>
            <div className="flex flex-wrap gap-2">
                {rows.map(s => (
                    <button
                        key={s.time}
                        type="button"
                        disabled={!s.available}
                        onClick={() => onChange(s.time)}
                        className={`px-3.5 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                            value === s.time
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : s.available
                                    ? 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50'
                                    : 'bg-gray-50 text-gray-300 border-gray-100 line-through cursor-not-allowed'
                        }`}
                    >
                        {s.time}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <div className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="flex items-center gap-3 flex-wrap">
                <input
                    type="date"
                    value={date}
                    min={todayKey()}
                    onChange={e => { onDateChange(e.target.value); onChange(''); }}
                    className="rounded-xl border border-gray-300 px-3.5 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                {date && <span className="text-sm text-gray-600">{formatDay(date, currentLanguage)}</span>}
            </div>
            {loading ? (
                <p className="text-sm text-gray-500">{t('appointments.form.loadingSlots')}</p>
            ) : closed ? (
                <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{t('appointments.form.closedDay')}</p>
            ) : slots.length === 0 ? (
                <p className="text-sm text-gray-500">{t('appointments.form.noSlots')}</p>
            ) : (
                <div className="space-y-4">
                    {group(t('appointments.form.morning'), morning)}
                    {group(t('appointments.form.afternoon'), afternoon)}
                </div>
            )}
        </div>
    );
};
